import { TFormHealth } from "..";
import { MGColors } from "../../../designSystem/colors";
import { MGCard } from "../../../designSystem/components/MGCard";
import { MGText } from "../../../designSystem/components/MGText";
import healthIcon from "../../../images/medical-cross-shield.svg";
import stethoscopeIcon from "../../../images/stethoscope-squre.svg";
import { MGLabel } from "../../../designSystem/components/MGLabel";
import { ChevronRight, InfoIcon, Plus } from "lucide-react";
import { MGSubtitle } from "../../../designSystem/components/MGSubtitle";
import { MGButton } from "../../../designSystem/components/MGButton";
import useMyMontepioContext from "../../../components/Context/useMyMontepioContext";
import styles from "../Health.module.css";
import { useCallback, useEffect, useState } from "react";
import { MGResponsiveSheet } from "../../../designSystem/components/MGResponsiveSheet";
import { MGInputRadioButton } from "../../../designSystem/components/MGInputRadioButton";
import planStyles from "./PlanStep.module.css";
import { MGMicrotext } from "../../../designSystem/components/MGMicrotext";
import { useSimulateMutation } from "../api";
import { MGLoader } from "../../../designSystem/components/MGLoader";
import { formatCurrency } from "../../../utils/formatCurrency";
import { MGNote } from "../../../designSystem/components/MGNote";
import { MGGenericError } from "../../../designSystem/components/MGGenericError";
import { BackgroundForm } from "../../../components/BackgroundForm";

type TPlanStepProps = {
  formHealth: TFormHealth;
  onContinue: (formHealth: TFormHealth) => void;
  onBack: VoidFunction;
};

export const PlanStep = ({ formHealth, onContinue, onBack }: TPlanStepProps) => {
  const { isMobileAuth } = useMyMontepioContext();

  const [openInfo, setOpenInfo] = useState(false);

  const [hasSupplementaryHealthPlanSubscription, setHasSupplementaryHealthPlanSubscription] =
    useState<boolean | undefined>(formHealth.supplementaryHealthPlan);

  const { data, mutate, isPending, isError } = useSimulateMutation("ANNUAL");

  const onDone = useCallback(() => {
    onContinue({
      ...formHealth,
      supplementaryHealthPlan: hasSupplementaryHealthPlanSubscription,
    });
  }, [formHealth, hasSupplementaryHealthPlanSubscription, onContinue]);

  const simulate = useCallback(() => {
    const { dateOfBirth, gender, moduleType, paymentFrequency } = formHealth;

    if (!dateOfBirth || !gender || !moduleType || !paymentFrequency) {
      return;
    }

    mutate({
      associate: {
        dateOfBirth,
        gender,
      },
      healthInsurancePlan: {
        supplementaryHealthPlan: true,
        supplementaryHealthPaymentFrequency: "ANNUAL",
        moduleType,
        paymentFrequency,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mutate,
    formHealth.dateOfBirth,
    formHealth.gender,
    formHealth.moduleType,
    formHealth.paymentFrequency,
  ]);

  useEffect(() => {
    simulate();
  }, [simulate]);

  if (isPending) {
    return (
      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <MGLoader />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <MGGenericError
          actionsAtBottom={isMobileAuth}
          title="Mensagem de Erro !"
          description="Explicação adicional, próximos passos e outras opções de contexto"
          primaryActionLabel="Tentar novamente"
          secondaryActionLabel="Voltar"
          onPrimaryAction={simulate}
          onSecondaryAction={onBack}
        />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flex: 1, flexDirection: "column" }}>
      <MGText
        style={{ marginTop: 8, marginBottom: 8 }}
        textStyleDefinition={"body-100-05"}
        color="text-primary-02">
        {"Prevenir é bom planear é ainda melhor"}
      </MGText>

      <MGText
        style={{ marginBottom: 32 }}
        textStyleDefinition={"body-100-03"}
        color="text-primary-02">
        {"Adicione o Plano Assegura saúde à sua modalidade para ..."}
      </MGText>

      <BackgroundForm style={{ marginBottom: isMobileAuth ? 32 : 0 }}>
        <MGCard shadow="10">
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div
              style={{
                display: "flex",
                width: 48,
                height: 48,
                backgroundColor: MGColors["02-70"].hex,
                borderRadius: 8,
                alignItems: "center",
                justifyContent: "center",
              }}>
              <img src={healthIcon} width={32} height={32} alt="" />
            </div>

            <div style={{ flex: 1 }}>
              <MGText textStyleDefinition="body-200-06" color="text-secondary-01">
                {"Plano Assegura Saúde"}
              </MGText>
              <MGLabel labelStyleDefinition="80-03" color="text-primary-02">
                {"Description"}
              </MGLabel>
            </div>

            <InfoIcon
              color={MGColors["neutral-600"].hex}
              size={24}
              onClick={() => setOpenInfo(true)}
            />
          </div>

          <div
            style={{ height: 1, backgroundColor: MGColors["neutral-100"].hex, margin: "16px 0" }}
          />

          <MGLabel labelStyleDefinition="80-05" color="text-primary-02" style={{ marginBottom: 8 }}>
            {"Coberturas"}
          </MGLabel>

          <div style={{ marginBottom: 8, display: "flex", alignItems: "center" }}>
            <ChevronRight size={24} color={MGColors["brand-01-200"].hex} />
            <MGLabel labelStyleDefinition="80-03" color="text-primary-02">
              {"Principais coberturas"}
            </MGLabel>
          </div>

          <div style={{ marginBottom: 8, display: "flex", alignItems: "center" }}>
            <ChevronRight size={24} color={MGColors["brand-01-200"].hex} />
            <MGLabel labelStyleDefinition="80-03" color="text-primary-02">
              {"Principais coberturas"}
            </MGLabel>
          </div>

          <div style={{ marginBottom: 16, display: "flex", alignItems: "center" }}>
            <ChevronRight size={24} color={MGColors["brand-01-200"].hex} />
            <MGLabel labelStyleDefinition="80-03" color="text-primary-02">
              {"Principais coberturas"}
            </MGLabel>
          </div>

          <MGLabel labelStyleDefinition="80-05" color="text-primary-02" style={{ marginBottom: 8 }}>
            {"Vantagens"}
          </MGLabel>

          <div style={{ marginBottom: 8, display: "flex", alignItems: "center" }}>
            <ChevronRight size={24} color={MGColors["brand-01-200"].hex} />
            <MGLabel labelStyleDefinition="80-03" color="text-primary-02">
              {"Principais Vantagens"}
            </MGLabel>
          </div>

          <div style={{ marginBottom: 16, display: "flex", alignItems: "center" }}>
            <ChevronRight size={24} color={MGColors["brand-01-200"].hex} />
            <MGLabel labelStyleDefinition="80-03" color="text-primary-02">
              {"Principais Vantagens"}
            </MGLabel>
          </div>

          <MGLabel labelStyleDefinition="80-05" color="text-primary-02" style={{ marginBottom: 8 }}>
            {"O que acrescenta à minha modalidade ?"}
          </MGLabel>

          <div style={{ marginBottom: 8, display: "flex", alignItems: "center" }}>
            <ChevronRight size={24} color={MGColors["brand-01-200"].hex} />
            <MGLabel labelStyleDefinition="80-03" color="text-primary-02">
              {"Texto"}
            </MGLabel>
          </div>

          <div style={{ display: "flex", alignItems: "center" }}>
            <ChevronRight size={24} color={MGColors["brand-01-200"].hex} />
            <MGLabel labelStyleDefinition="80-03" color="text-primary-02">
              {"Texto"}
            </MGLabel>
          </div>

          <div
            style={{ height: 1, backgroundColor: MGColors["neutral-200"].hex, margin: "24px 0" }}
          />

          <MGLabel labelStyleDefinition="80-03" color="text-primary-03" style={{ marginBottom: 8 }}>
            {"Capital subscrito"}
          </MGLabel>
          <MGLabel
            labelStyleDefinition="100-06"
            color="text-primary-02"
            style={{ marginBottom: 24 }}>
            {formatCurrency(data?.protection.capital ?? 0)}
          </MGLabel>

          <MGLabel labelStyleDefinition="80-03" color="text-primary-03" style={{ marginBottom: 8 }}>
            {"Quota anual do Plano"}
          </MGLabel>
          <MGLabel
            labelStyleDefinition="100-06"
            color="text-primary-02"
            style={{ marginBottom: 16 }}>
            {formatCurrency(data?.protection.annualAmount ?? 0)}
          </MGLabel>

          <MGLabel labelStyleDefinition="60-03" color="text-primary-02">
            {"Pode optar por fracionar"}
          </MGLabel>
        </MGCard>

        <MGSubtitle
          subtitleStyleDefinition="100-06"
          color="text-primary-01"
          style={{ marginTop: 32 }}>
          {"Titulo"}
        </MGSubtitle>

        <MGText
          textStyleDefinition="body-100-03"
          color="text-primary-02"
          style={{ marginTop: 24, marginBottom: 24 }}>
          {"Adicione o Plano Assegura saúde à sua modalidade para ..."}
        </MGText>

        <MGCard
          onClick={() => setHasSupplementaryHealthPlanSubscription(true)}
          style={{ marginBottom: 32 }}
          hasBorder={hasSupplementaryHealthPlanSubscription === true}>
          <div style={{ display: "flex", gap: 16, marginBottom: 32 }}>
            <MGInputRadioButton
              checked={hasSupplementaryHealthPlanSubscription === true}
              onChange={() => setHasSupplementaryHealthPlanSubscription(true)}
            />
            <div>
              <MGText textStyleDefinition="body-100-05" style={{ marginBottom: 4 }}>
                {"Subscrição da solução integrada"}
              </MGText>
              <MGLabel labelStyleDefinition="60-03" color="text-primary-03">
                {"Inclui o Plano Assegura a Saúde"}
              </MGLabel>
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div
              style={{
                display: "flex",
                width: 40,
                height: 40,
                backgroundColor: MGColors["02-70"].hex,
                borderRadius: 8,
                alignItems: "center",
                justifyContent: "center",
              }}>
              <img src={stethoscopeIcon} width={24} height={24} alt="" />
            </div>

            <div style={{ flex: 1 }}>
              <MGMicrotext microtextStyleDefinition="microcopy-100-05" color="text-primary-02">
                {"Modalidade de Saúde"}
              </MGMicrotext>
              <MGLabel labelStyleDefinition="60-03" color="text-primary-03">
                {"Subtitle"}
              </MGLabel>
            </div>
          </div>

          <div className={planStyles.planConnector} role="separator" aria-label="Mais">
            <span className={planStyles.planConnectorIcon} aria-hidden="true">
              <Plus size={16} strokeWidth={3} />
            </span>
          </div>

          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div
              style={{
                display: "flex",
                width: 40,
                height: 40,
                backgroundColor: MGColors["02-70"].hex,
                borderRadius: 8,
                alignItems: "center",
                justifyContent: "center",
              }}>
              <img src={healthIcon} width={24} height={24} alt="" />
            </div>

            <div style={{ flex: 1 }}>
              <MGMicrotext microtextStyleDefinition="microcopy-100-05" color="text-primary-02">
                {"Plano Assegura a Saúde"}
              </MGMicrotext>
              <MGLabel labelStyleDefinition="60-03" color="text-primary-03">
                {"Subtitle"}
              </MGLabel>
            </div>
          </div>
        </MGCard>

        <MGCard
          onClick={() => setHasSupplementaryHealthPlanSubscription(false)}
          hasBorder={hasSupplementaryHealthPlanSubscription === false}>
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            <MGInputRadioButton
              checked={hasSupplementaryHealthPlanSubscription === false}
              onChange={() => setHasSupplementaryHealthPlanSubscription(false)}
            />
            <MGText textStyleDefinition="body-100-05">
              {"Subscrição apenas da Modalidade de Saúde"}
            </MGText>
          </div>
        </MGCard>

        {hasSupplementaryHealthPlanSubscription === false && (
          <MGNote
            description="... Deve subscrever agora ou ... a próxima data disponivél será ... ( + 1 ano)"
            backgroundColor="transparent"
            icon="info"
            style={{ marginTop: 16 }}
          />
        )}
      </BackgroundForm>

      <MGButton
        disabled={hasSupplementaryHealthPlanSubscription === undefined}
        fullWidth={isMobileAuth}
        className={`${styles.buttonFooter} ${isMobileAuth ? styles.buttonFooterFixed : ""}`}
        onClick={onDone}>
        {"Continuar"}
      </MGButton>

      <MGResponsiveSheet
        open={openInfo}
        title={"Plano Assegura Saúde"}
        onClose={() => setOpenInfo(false)}>
        <MGText textStyleDefinition="body-100-03">
          {
            "Detalhes e informação adicional\nDetalhes e informação adicional\nDetalhes e informação adicional\nDetalhes e informação adicional"
          }
        </MGText>
      </MGResponsiveSheet>
    </div>
  );
};
